import type { SearchSummary } from '@tunasong/models'
import { usePlugins } from '@tunasong/plugin-lib'
import { VisibilityTrigger } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { MouseEvent, ReactElement, ReactNode } from 'react'
import { useEffect } from 'react'
import { getAvatarOrIcon } from './avatar.js'
import styles from './search-bar.module.css'
import { optionId } from './search-listbox-ids.js'
import { SearchResultContent } from './search-result-item.js'

export interface SearchListboxProps<T extends SearchSummary> {
  id: string
  label: string
  entities: T[]
  /** The option the combobox points at; -1 for none */
  activeIndex: number
  /** After the options, inside the list: the scoped "{n} more in other spaces" line */
  footer?: ReactNode
  /** Search's dialog has a fixed results height; a popup sizes the list itself */
  fixedHeight?: boolean
  /** A visible heading over the options, e.g. Recent */
  heading?: string
  /** The query, whose words are marked in each name (#2555 M6) */
  highlight?: string
  /** In place of the options when there are none */
  empty?: ReactNode
  /** Earlier results under an error: shown faded, and hidden from assistive tech */
  dimmed?: boolean
  renderAction?(entity: T, active: boolean): ReactElement | null
  onOpen(entity: T, options: { newTab: boolean }): void
  onEndOfResults?(): Promise<void>
}

/**
 * Search's results as a WAI-ARIA listbox (#2555): focus stays in the combobox, which points at the active option.
 * It replaces a keydown-driven menu that screen readers could not follow.
 */
export const SearchListbox = <T extends SearchSummary>(props: SearchListboxProps<T>) => {
  const {
    id,
    label,
    entities,
    activeIndex,
    renderAction,
    onOpen,
    onEndOfResults,
    footer,
    fixedHeight = true,
    heading,
    highlight,
    empty,
    dimmed = false,
  } = props
  const plugins = usePlugins('all')

  /** Keep the active option in view as the arrows move it */
  useEffect(() => {
    if (activeIndex >= 0) {
      document.getElementById(optionId(id, activeIndex))?.scrollIntoView?.({ block: 'nearest' })
    }
  }, [activeIndex, id])

  const handleClick = (entity: T) => (ev: MouseEvent) => onOpen(entity, { newTab: ev.metaKey || ev.ctrlKey })

  return (
    <div className={cn(styles.results, { [styles.resultsFixed]: fixedHeight, [styles.dimmed]: dimmed })}>
      {heading && entities.length > 0 ? (
        <h3 className={styles.heading} aria-hidden="true">
          {heading}
        </h3>
      ) : null}
      {empty}
      <div role="listbox" id={id} aria-label={label} className={styles.listbox} aria-hidden={dimmed || undefined}>
        {entities.map((entity, index) => (
          <div
            key={entity.id}
            role="option"
            id={optionId(id, index)}
            aria-selected={index === activeIndex}
            className={cn(styles.option, { [styles.optionActive]: index === activeIndex })}
            onClick={handleClick(entity)}
          >
            <SearchResultContent
              element={entity}
              highlight={highlight}
              icon={plugins ? getAvatarOrIcon(entity, plugins) : undefined}
              breadCrumbLinks={false}
            />
            {renderAction ? (
              <span className={styles.optionAction}>{renderAction(entity, index === activeIndex)}</span>
            ) : null}
          </div>
        ))}
      </div>
      {/* After the listbox, not in it: a listbox holds only options */}
      {entities.length > 0 ? <VisibilityTrigger onVisible={onEndOfResults} /> : null}
      {footer}
    </div>
  )
}
